import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BRAND } from "../mock/mock";
import { Boxes, Megaphone, Image as ImageIcon, Sparkles, Search, Plus, Trash2, Save, Download, RefreshCw, Lock, ArrowLeft, Eye, EyeOff, Upload, Info, ShieldCheck, ExternalLink } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import ImageInput from "../components/ImageInput";

const LS_KEY = "ardf_site_override_v1";
const LS_AUTH = "ardf_editor_authed_v1";
const DEFAULT_PASSWORD = "Admin@123"; // user can change in Settings tab
const LS_PASSWORD_HASH = "ardf_editor_pw_v1";

async function sha256(text) {
  const buf = new TextEncoder().encode(text);
  const h = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(h)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

const CATEGORIES = ["almonds", "cashews", "raisins", "pistachios", "walnuts", "dates", "apricots", "berries", "seeds", "breakfast", "spices", "exotic"];

const emptyProduct = () => ({
  id: `p${Date.now().toString(36)}`,
  slug: "",
  name: "",
  category: "almonds",
  image: "",
  price: 0,
  mrp: 0,
  discount: 0,
  weight: "1 kg",
  origin: "",
  rating: 4.7,
  tags: [],
  description: "",
  featured: false,
  in_stock: true,
});

const EditorPage = () => {
  const { toast } = useToast();
  const [authed, setAuthed] = useState(() => sessionStorage.getItem(LS_AUTH) === "1");
  const [pw, setPw] = useState("");
  const [tab, setTab] = useState("products");
  const [site, setSite] = useState({ products: [], announcements: [], hero_slides: [], promo_cards: [] });
  const [ready, setReady] = useState(false);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const raw = localStorage.getItem(LS_KEY);
        if (raw) {
          setSite(JSON.parse(raw));
          setReady(true);
          return;
        }
      } catch (e) {}
      try {
        const res = await fetch(`${process.env.PUBLIC_URL || ""}/data/site.json`, { cache: "no-store" });
        if (res.ok) setSite(await res.json());
      } catch (e) {}
      setReady(true);
    };
    load();
  }, []);

  const login = async (e) => {
    e?.preventDefault();
    const stored = localStorage.getItem(LS_PASSWORD_HASH);
    const target = stored || (await sha256(DEFAULT_PASSWORD));
    const attempt = await sha256(pw);
    if (attempt === target) {
      sessionStorage.setItem(LS_AUTH, "1");
      setAuthed(true);
      setPw("");
    } else {
      toast({ title: "Wrong password", description: "Try again." });
    }
  };

  const logout = () => {
    sessionStorage.removeItem(LS_AUTH);
    setAuthed(false);
  };

  const savePreview = () => {
    localStorage.setItem(LS_KEY, JSON.stringify(site));
    toast({ title: "Preview saved", description: "Open the site in another tab to see your changes." });
  };

  const clearPreview = () => {
    if (!window.confirm("Clear preview and revert to the shipped data?")) return;
    localStorage.removeItem(LS_KEY);
    window.location.reload();
  };

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(site, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "site.json";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({ title: "Downloaded site.json", description: "Upload this to /data/site.json on your host to publish." });
  };

  const importJson = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result);
        if (!data || !Array.isArray(data.products)) throw new Error("Invalid file");
        setSite(data);
        toast({ title: "Imported", description: "File loaded into the editor. Click Save preview to see it on the site." });
      } catch (e) {
        toast({ title: "Invalid file", description: e.message });
      }
    };
    reader.readAsText(file);
  };

  const setProducts = (list) => setSite({ ...site, products: list });
  const setContent = (key, list) => setSite({ ...site, [key]: list });

  const filtered = useMemo(() => {
    let list = site.products || [];
    if (catFilter !== "all") list = list.filter((p) => p.category === catFilter);
    if (q) list = list.filter((p) => (p.name || "").toLowerCase().includes(q.toLowerCase()));
    return list;
  }, [site.products, q, catFilter]);

  const upsertProduct = (p) => {
    const list = [...(site.products || [])];
    const idx = list.findIndex((x) => x.id === p.id);
    // recompute discount if mrp/price set
    if (p.price && p.mrp && p.mrp > p.price) p.discount = Math.round(((p.mrp - p.price) / p.mrp) * 100);
    if (idx >= 0) list[idx] = p;
    else list.push(p);
    setProducts(list);
    setEditing(null);
  };

  const deleteProduct = (id) => {
    if (!window.confirm("Delete this product?")) return;
    setProducts((site.products || []).filter((p) => p.id !== id));
  };

  if (!ready) return <main className="min-h-screen flex items-center justify-center bg-cream text-[#6b4f39]">Loading editor…</main>;

  if (!authed) return (
    <main className="min-h-screen bg-[#2a1a15] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-1.5 text-[#f2e7d1] hover:text-gold text-sm mb-6"><ArrowLeft className="h-4 w-4" /> Back to site</Link>
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt="" className="h-12 w-12 object-contain" />
            <div>
              <div className="font-serif-brand font-bold text-brand text-lg leading-tight">Agrawal Rich</div>
              <div className="text-[10px] tracking-[0.25em] text-olive">CONTENT EDITOR</div>
            </div>
          </div>
          <h1 className="font-serif-brand text-2xl font-bold text-[#2a1a15] mt-6">Enter password</h1>
          <p className="text-sm text-[#6b4f39] mt-1">Editor is only accessible from this device.</p>

          <form onSubmit={login} className="mt-6 space-y-3">
            <div className="relative">
              <Lock className="h-4 w-4 text-[#a08774] absolute left-3 top-1/2 -translate-y-1/2" />
              <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" className="h-11 pl-9 bg-cream border-[#e3d3ad]" required />
            </div>
            <Button type="submit" className="w-full h-11 bg-brand hover:bg-[#6c1414] text-white font-semibold"><ShieldCheck className="h-4 w-4 mr-1.5" /> Enter editor</Button>
          </form>

          <p className="mt-5 text-[12px] text-[#6b4f39] bg-[#f9f1de] rounded-lg p-3">Default password: <b>Admin@123</b> — change it in the Settings tab after logging in.</p>
        </div>
      </div>
    </main>
  );

  return (
    <main className="min-h-screen bg-[#f7f1e2]">
      <header className="bg-[#2a1a15] text-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <Link to="/editor" className="flex items-center gap-3">
            <img src={BRAND.logo} alt="" className="h-9 w-9 object-contain bg-white/95 rounded-full p-0.5" />
            <div className="leading-tight">
              <div className="font-serif-brand text-white text-base font-bold">Agrawal Rich</div>
              <div className="text-[9.5px] tracking-[0.25em] text-gold">CONTENT EDITOR</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/" target="_blank" className="hidden md:inline-flex items-center gap-1 text-[13px] text-[#f2e7d1] hover:text-gold px-3 py-2"><ExternalLink className="h-4 w-4" /> View site</Link>
            <Button onClick={savePreview} className="h-9 bg-olive hover:bg-[#5a6a2e] text-white"><Save className="h-4 w-4 mr-1.5" /> Save preview</Button>
            <Button onClick={downloadJson} className="h-9 bg-gold hover:bg-[#b6944f] text-[#2a1a15]"><Download className="h-4 w-4 mr-1.5" /> Download site.json</Button>
            <button onClick={logout} className="hidden md:inline-flex items-center gap-1.5 text-[13px] text-[#f2e7d1] hover:text-gold px-3 py-2">Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Publish flow hint */}
        <div className="bg-[#eef7e6] border border-olive/30 text-[#3f4f24] rounded-2xl p-4 flex flex-col md:flex-row md:items-center gap-3">
          <Info className="h-5 w-5 shrink-0 text-olive" />
          <div className="text-[13.5px] leading-snug">
            <b>How to publish changes:</b> 1) Make edits below. 2) Click <b>Save preview</b> to see them in this browser. 3) When ready, click <b>Download site.json</b> and upload the file to your host, replacing <code>/data/site.json</code>.
          </div>
          <div className="ml-auto flex items-center gap-2">
            <label className="inline-flex items-center gap-1.5 text-[12.5px] bg-white border border-[#e3d3ad] px-3 py-1.5 rounded cursor-pointer hover:bg-[#f9f1de]">
              <Upload className="h-4 w-4" /> Import
              <input type="file" accept="application/json" className="hidden" onChange={(e) => e.target.files?.[0] && importJson(e.target.files[0])} />
            </label>
            <button onClick={clearPreview} className="text-[12.5px] bg-white border border-[#e3d3ad] px-3 py-1.5 rounded hover:bg-[#f9f1de] inline-flex items-center gap-1.5"><RefreshCw className="h-4 w-4" /> Reset preview</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-5 flex items-center gap-1 border-b border-[#efe4c9] overflow-x-auto">
          {[
            { id: "products", l: `Products (${(site.products || []).length})`, i: Boxes },
            { id: "announcements", l: "Announcements", i: Sparkles },
            { id: "hero_slides", l: "Hero Slides", i: ImageIcon },
            { id: "promo_cards", l: "Promo Cards", i: Megaphone },
            { id: "settings", l: "Settings", i: Lock },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id ? "border-brand text-brand" : "border-transparent text-[#6b4f39] hover:text-[#2a1a15]"}`}>
              <span className="inline-flex items-center gap-1.5"><t.i className="h-4 w-4" /> {t.l}</span>
            </button>
          ))}
        </div>

        {tab === "products" && (
          <section className="mt-5">
            <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="h-4 w-4 text-[#a08774] absolute left-3 top-1/2 -translate-y-1/2" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products..." className="h-10 pl-9 bg-white border-[#e3d3ad]" />
              </div>
              <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className="h-10 bg-white border border-[#e3d3ad] rounded-md px-3 text-sm capitalize">
                <option value="all">All categories</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <Button onClick={() => setEditing(emptyProduct())} className="h-10 bg-brand hover:bg-[#6c1414] text-white"><Plus className="h-4 w-4 mr-1.5" /> New product</Button>
            </div>

            <div className="bg-white rounded-2xl border border-[#efe4c9] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#f9f1de] text-[#3a2a20]">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold">Product</th>
                      <th className="text-left px-4 py-3 font-semibold">Category</th>
                      <th className="text-left px-4 py-3 font-semibold">Weight</th>
                      <th className="text-right px-4 py-3 font-semibold">Price</th>
                      <th className="text-right px-4 py-3 font-semibold">MRP</th>
                      <th className="text-right px-4 py-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((p) => (
                      <tr key={p.id} className="border-t border-[#efe4c9] hover:bg-[#faf3e2]">
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2.5">
                            <img src={p.image} alt="" className="h-10 w-10 rounded object-cover" />
                            <div>
                              <div className="font-medium text-[#2a1a15]">{p.name}</div>
                              <div className="text-[11.5px] text-[#6b4f39]">{p.origin}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2.5 capitalize">{p.category}</td>
                        <td className="px-4 py-2.5">{p.weight}</td>
                        <td className="px-4 py-2.5 text-right font-semibold">₹{Number(p.price).toLocaleString("en-IN")}</td>
                        <td className="px-4 py-2.5 text-right text-[#a08774] line-through">₹{Number(p.mrp || 0).toLocaleString("en-IN")}</td>
                        <td className="px-4 py-2.5 text-right">
                          <button onClick={() => setEditing({ ...p })} className="p-1.5 text-[#6b4f39] hover:text-brand" title="Edit"><Boxes className="h-4 w-4" /></button>
                          <button onClick={() => deleteProduct(p.id)} className="p-1.5 text-[#6b4f39] hover:text-brand" title="Delete"><Trash2 className="h-4 w-4" /></button>
                        </td>
                      </tr>
                    ))}
                    {filtered.length === 0 && (<tr><td colSpan={6} className="text-center py-10 text-[#6b4f39]">No products found</td></tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {tab === "announcements" && (
          <ListEditor
            title="Announcement Bar"
            items={site.announcements || []}
            onChange={(list) => setContent("announcements", list)}
            newItem={() => ({ id: `a${Date.now().toString(36)}`, text: "", active: true })}
            fields={[{ name: "text", label: "Announcement text", full: true }]}
          />
        )}

        {tab === "hero_slides" && (
          <ListEditor
            title="Hero Slides"
            items={site.hero_slides || []}
            onChange={(list) => setContent("hero_slides", list)}
            newItem={() => ({ id: `h${Date.now().toString(36)}`, active: true, tag: "", title: "", subtitle: "", cta: "Shop now", to: "/shop", image: "", external: false })}
            fields={[
              { name: "tag", label: "Small tag" },
              { name: "title", label: "Headline" },
              { name: "subtitle", label: "Subtitle", type: "textarea", full: true },
              { name: "cta", label: "Button text" },
              { name: "to", label: "Link (/shop or full URL)", full: true },
              { name: "image", label: "Image URL", full: true, isImage: true },
              { name: "external", label: "External link (opens new tab)", type: "checkbox" },
            ]}
          />
        )}

        {tab === "promo_cards" && (
          <ListEditor
            title="Promo Cards"
            items={site.promo_cards || []}
            onChange={(list) => setContent("promo_cards", list)}
            newItem={() => ({ id: `p${Date.now().toString(36)}`, active: true, eyebrow: "", title: "", code: "", to: "/shop", image: "", tint: "#8B1A1A" })}
            fields={[
              { name: "eyebrow", label: "Small label" },
              { name: "title", label: "Title" },
              { name: "code", label: "Discount code (optional)" },
              { name: "to", label: "Link" },
              { name: "tint", label: "Tint color (hex, e.g. #8B1A1A)" },
              { name: "image", label: "Image URL", full: true, isImage: true },
            ]}
          />
        )}

        {tab === "settings" && <PasswordSettings toast={toast} />}
      </div>

      {editing && <ProductDialog product={editing} onCancel={() => setEditing(null)} onSave={upsertProduct} />}
    </main>
  );
};

const ProductDialog = ({ product, onCancel, onSave }) => {
  const [p, setP] = useState(product);
  const set = (k, v) => setP({ ...p, [k]: v });
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" onClick={onCancel}>
      <div className="bg-white rounded-2xl w-full max-w-2xl p-5 md:p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="font-serif-brand text-xl font-bold text-[#2a1a15]">{p.id && p.name ? "Edit product" : "New product"}</div>
        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <div className="sm:col-span-2"><label className="text-[12.5px] font-medium">Name</label><Input value={p.name || ""} onChange={(e) => set("name", e.target.value)} /></div>
          <div><label className="text-[12.5px] font-medium">Category</label>
            <select value={p.category} onChange={(e) => set("category", e.target.value)} className="h-10 w-full border border-[#e3d3ad] rounded-md px-3 mt-1 bg-white capitalize">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div><label className="text-[12.5px] font-medium">Weight</label><Input value={p.weight || ""} onChange={(e) => set("weight", e.target.value)} /></div>
          <div><label className="text-[12.5px] font-medium">Price (₹)</label><Input type="number" value={p.price || 0} onChange={(e) => set("price", parseFloat(e.target.value) || 0)} /></div>
          <div><label className="text-[12.5px] font-medium">MRP (₹)</label><Input type="number" value={p.mrp || 0} onChange={(e) => set("mrp", parseFloat(e.target.value) || 0)} /></div>
          <div><label className="text-[12.5px] font-medium">Origin</label><Input value={p.origin || ""} onChange={(e) => set("origin", e.target.value)} /></div>
          <div><label className="text-[12.5px] font-medium">Rating</label><Input type="number" step="0.1" value={p.rating || 4.7} onChange={(e) => set("rating", parseFloat(e.target.value) || 4.7)} /></div>
          <div className="sm:col-span-2"><label className="text-[12.5px] font-medium">Image</label>
            <div className="mt-1"><ImageInput value={p.image || ""} onChange={(v) => set("image", v)} height="h-28" /></div>
          </div>
          <div className="sm:col-span-2"><label className="text-[12.5px] font-medium">Tags (comma separated)</label><Input value={Array.isArray(p.tags) ? p.tags.join(", ") : (p.tags || "")} onChange={(e) => set("tags", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="PREMIUM, BESTSELLER" /></div>
          <div className="sm:col-span-2"><label className="text-[12.5px] font-medium">Description</label><Textarea rows={3} value={p.description || ""} onChange={(e) => set("description", e.target.value)} /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!p.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={p.in_stock !== false} onChange={(e) => set("in_stock", e.target.checked)} /> In stock</label>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={() => onSave(p)} className="bg-brand hover:bg-[#6c1414] text-white"><Save className="h-4 w-4 mr-1.5" /> Save</Button>
        </div>
      </div>
    </div>
  );
};

const ListEditor = ({ title, items, onChange, newItem, fields }) => {
  const add = () => onChange([...(items || []), newItem()]);
  const upd = (idx, key, val) => {
    const list = [...items];
    list[idx] = { ...list[idx], [key]: val };
    onChange(list);
  };
  const rm = (idx) => { const list = [...items]; list.splice(idx, 1); onChange(list); };

  return (
    <section className="mt-5">
      <div className="flex items-center justify-between mb-4">
        <div className="font-serif-brand text-xl font-bold text-[#2a1a15]">{title}</div>
        <Button onClick={add} className="h-9 bg-brand hover:bg-[#6c1414] text-white"><Plus className="h-4 w-4 mr-1.5" /> Add</Button>
      </div>
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={it.id || i} className="bg-white rounded-2xl border border-[#efe4c9] p-4 md:p-5">
            <div className="flex items-center justify-between mb-3">
              <button onClick={() => upd(i, "active", !(it.active !== false))} className={`inline-flex items-center gap-1 text-[12px] font-semibold px-2.5 py-1 rounded ${it.active !== false ? "bg-olive/15 text-olive" : "bg-[#efe4c9] text-[#6b4f39]"}`}>
                {it.active !== false ? <><Eye className="h-3.5 w-3.5" /> ACTIVE</> : <><EyeOff className="h-3.5 w-3.5" /> HIDDEN</>}
              </button>
              <button onClick={() => rm(i)} className="text-[#a08774] hover:text-brand p-1.5"><Trash2 className="h-4 w-4" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {fields.map((f) => (
                <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
                  <label className="text-[12.5px] font-medium">{f.label}</label>
                  {f.type === "textarea" ? (
                    <Textarea rows={2} value={it[f.name] || ""} onChange={(e) => upd(i, f.name, e.target.value)} className="mt-1 bg-cream border-[#e3d3ad]" />
                  ) : f.type === "checkbox" ? (
                    <label className="mt-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={!!it[f.name]} onChange={(e) => upd(i, f.name, e.target.checked)} /> {f.label}</label>
                  ) : f.isImage ? (
                    <div className="mt-1"><ImageInput value={it[f.name] || ""} onChange={(v) => upd(i, f.name, v)} height="h-20" /></div>
                  ) : (
                    <>
                      <Input value={it[f.name] || ""} onChange={(e) => upd(i, f.name, e.target.value)} className="mt-1 h-10 bg-cream border-[#e3d3ad]" />
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="bg-white rounded-2xl border border-[#efe4c9] p-10 text-center text-[#6b4f39] text-sm">No items yet. Click <b>Add</b> to create one.</div>}
      </div>
    </section>
  );
};

const PasswordSettings = ({ toast }) => {
  const [cur, setCur] = useState("");
  const [n1, setN1] = useState("");
  const [n2, setN2] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (n1 !== n2) return toast({ title: "Passwords don't match" });
    if (n1.length < 6) return toast({ title: "Too short", description: "Minimum 6 characters." });
    const stored = localStorage.getItem(LS_PASSWORD_HASH);
    const target = stored || (await sha256(DEFAULT_PASSWORD));
    const attempt = await sha256(cur);
    if (attempt !== target) return toast({ title: "Current password is wrong" });
    const nh = await sha256(n1);
    localStorage.setItem(LS_PASSWORD_HASH, nh);
    setCur(""); setN1(""); setN2("");
    toast({ title: "Password updated", description: "Applies to this device only." });
  };

  return (
    <section className="mt-5 max-w-xl">
      <div className="font-serif-brand text-xl font-bold text-[#2a1a15] mb-2">Editor password</div>
      <p className="text-[13px] text-[#6b4f39] mb-4">Password is stored locally in this browser (SHA-256 hash). It gates access to the editor only — site visitors don't see it.</p>
      <form onSubmit={submit} className="bg-white rounded-2xl border border-[#efe4c9] p-5 space-y-3">
        <div><label className="text-[12.5px] font-medium">Current password</label><Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="mt-1 h-11 bg-cream border-[#e3d3ad]" required /></div>
        <div><label className="text-[12.5px] font-medium">New password</label><Input type="password" value={n1} onChange={(e) => setN1(e.target.value)} className="mt-1 h-11 bg-cream border-[#e3d3ad]" required minLength={6} /></div>
        <div><label className="text-[12.5px] font-medium">Confirm new password</label><Input type="password" value={n2} onChange={(e) => setN2(e.target.value)} className="mt-1 h-11 bg-cream border-[#e3d3ad]" required minLength={6} /></div>
        <Button type="submit" className="h-11 bg-brand hover:bg-[#6c1414] text-white font-semibold"><Save className="h-4 w-4 mr-1.5" /> Update password</Button>
      </form>
    </section>
  );
};

export default EditorPage;
